import React, { useState, useEffect, useContext } from 'react';
import { Box, Stack, TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { HomeContext } from '../utils/contexts/homeContext';
import { NurseFormContext } from '../utils/contexts/nurseFormContext';
import {
    GetDayJSValueFromUTCDate, GetDateFromDayJSValue, NurseVacationTrackerAPIUrl
} from '../utils/extensions/functionEx';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

export default function NurseVacationForm(props) {
    const { isOpen, nurseId, handleClose, selectedVacation } = props;

    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);
    const { SetNurseVacations } = useContext(NurseFormContext);

    const [cleared, setCleared] = useState(false);
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());
    const [notes, setNotes] = useState('');
    const [isSaveInProgress, setIsSaveInProgress] = useState(false);
    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if (isOpen && selectedVacation && selectedVacation.id > 0) {
            setStartDate(GetDayJSValueFromUTCDate(selectedVacation.start_date));
            setEndDate(GetDayJSValueFromUTCDate(selectedVacation.end_date));
            setNotes(selectedVacation.remarks);
        }
    }, [isOpen, selectedVacation]);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [cleared]);

    const handleSaveClick = async () => {
        try {
            if (!startDate || !endDate || startDate.isAfter(endDate)) {
                OpenErrorAlert('Enter valid vacation details.')
                return;
            }
            setIsSaveInProgress(true);

            const tempVacation = {
                id: 0,
                nurse_id: nurseId,
                start_date: GetDateFromDayJSValue(startDate),
                end_date: GetDateFromDayJSValue(endDate),
                remarks: notes.trim()
            };
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            let response;

            if (selectedVacation && selectedVacation.id > 0) {
                tempVacation.id = selectedVacation.id;

                response = await fetch(NurseVacationTrackerAPIUrl(homeState.loggedUser.companyCode), {
                    method: "PUT",
                    headers: headers,
                    body: JSON.stringify(tempVacation)
                });
            } else {
                response = await fetch(NurseVacationTrackerAPIUrl(homeState.loggedUser.companyCode), {
                    method: "POST",
                    headers: headers,
                    body: JSON.stringify(tempVacation)
                });
            }
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.ok) {
                OpenSuccessAlert('Nurse vacation saved successfully.');
                await fetchVacationData();
                handleClose();

                return;
            }
            ShowAPIError(response);
        }
        catch (error) {
            OpenErrorAlert(error)
        }
        finally {
            setIsSaveInProgress(false);
        }
    }

    async function fetchVacationData() {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseVacationTrackerAPIUrl(homeState.loggedUser.companyCode, nurseId), {
                method: "GET",
                headers: headers
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                SetNurseVacations(await response.json());
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
    }


    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-in"}>
                <BootstrapDialog
                    onClose={handleClose}
                    open={isOpen}
                    fullWidth={true}
                    maxWidth={"sm"}
                >
                    <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title-nurse-vacation-tracker-form">
                        Nurse Vacation Tracker Form
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                        <Stack>
                            <Box sx={{ paddingTop: '4px' }}>
                                <Stack spacing={2}>
                                    <Grid container spacing={2}>
                                        <Grid size={6}>
                                            <DatePicker format="DD/MM/YYYY"
                                                label="Start Date" readOnly={false}
                                                slotProps={{
                                                    textField: {
                                                        size: "small", fullWidth: true, required: true,
                                                        error: !startDate ? true : false,
                                                        helperText: !startDate ? 'Enter start date' : ''
                                                    },
                                                    field: {
                                                        clearable: true,
                                                        onClear: () => setCleared(true)
                                                    }
                                                }}
                                                value={startDate} minDate={dayjs().add(-3, 'day')}
                                                onChange={(newValue) => setStartDate(newValue)}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <DatePicker format="DD/MM/YYYY"
                                                label="End Date" readOnly={false}
                                                slotProps={{
                                                    textField: {
                                                        size: "small", fullWidth: true, required: true,
                                                        error: !endDate ? true : false,
                                                        helperText: !endDate ? 'Enter end date' : ''
                                                    },
                                                    field: {
                                                        clearable: true,
                                                        onClear: () => setCleared(true)
                                                    }
                                                }}
                                                value={endDate} minDate={startDate.add(1, 'day')}
                                                onChange={(newValue) => setEndDate(newValue)}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextField size="small" fullWidth
                                                label="Remarks"
                                                value={notes}
                                                onChange={(event) => {
                                                    setNotes(event.target.value);
                                                }}
                                                slotProps={{
                                                    input: {
                                                        readOnly: false
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Stack>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        {
                            isSaveInProgress && <CircularProgress size="30px" />
                        }
                        <Button variant="contained" onClick={handleSaveClick}>
                            Save
                        </Button>
                    </DialogActions>
                </BootstrapDialog> </LocalizationProvider>
        </React.Fragment>

    );
}
